import React, { useState } from 'react';

const API = 'https://familybandhan-backend-933326390640.asia-south1.run.app';

const GEN_LABELS = {
  '-3': 'Great-grandparents',
  '-2': 'Grandparents',
  '-1': 'Parents',
  '0': 'Your generation',
  '1': 'Children',
  '2': 'Grandchildren',
  '3': 'Great-grandchildren',
};

export default function MemberPanel({ member, familyEmail, onClose, onMemberUpdated }) {
  const [name, setName] = useState('');
  const [relation, setRelation] = useState('');
  const [story, setStory] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoMsg, setPhotoMsg] = useState('');
  const [dob, setDob] = useState('');
  const [dobMonthDay, setDobMonthDay] = useState('');
  const [dobKnownYear, setDobKnownYear] = useState(true);
  const fileInputRef = React.useRef(null);

  React.useEffect(() => {
    if (member) {
      setName(member.name || '');
      setRelation(member.relation || '');
      setStory(member.story || '');
      setNotes(member.specialNotes || '');
      setSaveMsg('');
      setPhotoMsg('');
      setDob(member.dob || '');
      setDobMonthDay(member.dobMonthDay || '');
      setDobKnownYear(member.dob ? true : (member.dobMonthDay ? false : true));
    }
  }, [member]);

  if (!member) return null;

  const isCreator = member.isCreator;
  const avatarBg = isCreator
    ? '#FEF9C3'
    : member.gender === 'male'
    ? '#DBEAFE'
    : '#FCE7F3';
  const avatarText = isCreator
    ? '#854D0E'
    : member.gender === 'male'
    ? '#1D4ED8'
    : '#BE185D';

  const initials = member.name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const genLabel = GEN_LABELS[String(member.generation)] || `Gen ${member.generation}`;
  const branchLabel = member.branch === 'mat' ? 'Maternal side' : 'Paternal side';

  async function handlePhotoUpload(e) {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setPhotoMsg('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setPhotoMsg('Photo must be under 5MB');
      return;
    }

    setPhotoUploading(true);
    setPhotoMsg('');

    try {
      const encoded = encodeURIComponent(familyEmail);

      const urlRes = await fetch(`${API}/api/members/${encoded}/${member.memberId}/photo-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileName: `profile_${Date.now()}.${file.name.split('.').pop()}`,
          fileType: file.type,
        }),
      });

      if (!urlRes.ok) throw new Error('Could not get upload URL');
      const { signedUrl, publicUrl, accessToken } = await urlRes.json();

   // Step 2 — Upload to GCS using access token
const uploadRes = await fetch(signedUrl, {
  method: 'POST',
  headers: {
    'Content-Type': file.type,
    'Authorization': `Bearer ${accessToken}`,
    'X-Goog-Upload-Content-Type': file.type,
  },
  body: file,
});
      if (!uploadRes.ok) throw new Error('Photo upload failed');

      const saveRes = await fetch(`${API}/api/members/${encoded}/${member.memberId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profilePhotoUrl: publicUrl }),
      });

      if (!saveRes.ok) throw new Error('Could not save photo URL');

      setPhotoMsg('Photo saved ✓');
      if (onMemberUpdated) onMemberUpdated();

    } catch (err) {
      console.error('Photo upload error:', err);
      setPhotoMsg('Upload failed — try again');
    } finally {
      setPhotoUploading(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    setSaveMsg('');
    try {
      const encoded = encodeURIComponent(familyEmail);
      const res = await fetch(`${API}/api/members/${encoded}/${member.memberId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim() || member.name,
          relation: relation.trim(),
          story,
          specialNotes: notes,
          dob: dobKnownYear ? dob : '',
          dobMonthDay: !dobKnownYear ? dobMonthDay : '',
        }),
      });
      if (!res.ok) throw new Error('Save failed');
      setSaveMsg('Saved ✓');
      if (onMemberUpdated) onMemberUpdated();
    } catch (err) {
      setSaveMsg('Save failed — try again');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      {/* Dark overlay */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.18)',
          zIndex: 200,
        }}
      />

      {/* Panel */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: '320px',
          height: '100vh',
          background: '#fff',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.13)',
          zIndex: 201,
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '18px 16px 14px',
          borderBottom: '1px solid #F3F4F6',
        }}>
          <div style={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            background: avatarBg,
            color: avatarText,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: 16,
            flexShrink: 0,
            border: isCreator ? '2.5px solid #D4A017' : '1.5px solid #E5E7EB',
            overflow: 'hidden',
          }}>
            {member.profilePhotoUrl ? (
              <img
                src={member.profilePhotoUrl}
                alt={member.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              initials
            )}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontWeight: 600,
              fontSize: 15,
              color: '#111827',
              wordBreak: 'break-word',
              lineHeight: 1.3,
            }}>
              {member.name}
            </div>
            <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
              {member.relation || member.relationToCreator || ''}
            </div>
          </div>

          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 20,
              color: '#9CA3AF',
              padding: '4px',
              lineHeight: 1,
              flexShrink: 0,
            }}
          >
            ×
          </button>
        </div>

        {/* Tags */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px',
          padding: '12px 16px',
          borderBottom: '1px solid #F3F4F6',
        }}>
          <span style={{
            background: member.gender === 'male' ? '#DBEAFE' : '#FCE7F3',
            color: member.gender === 'male' ? '#1D4ED8' : '#BE185D',
            borderRadius: 20,
            padding: '3px 10px',
            fontSize: 11,
            fontWeight: 500,
          }}>
            {member.gender === 'male' ? '♂ Male' : '♀ Female'}
          </span>

          <span style={{
            background: '#FEF9C3',
            color: '#854D0E',
            borderRadius: 20,
            padding: '3px 10px',
            fontSize: 11,
            fontWeight: 500,
          }}>
            {genLabel}
          </span>

          <span style={{
            background: '#F3F4F6',
            color: '#374151',
            borderRadius: 20,
            padding: '3px 10px',
            fontSize: 11,
            fontWeight: 500,
          }}>
            {branchLabel}
          </span>

          {member.isDeceased && (
            <span style={{
              background: '#E5E7EB',
              color: '#4B5563',
              borderRadius: 20,
              padding: '3px 10px',
              fontSize: 11,
              fontWeight: 500,
            }}>
              🪔 Deceased
            </span>
          )}

          {isCreator && (
            <span style={{
              background: '#FEF9C3',
              color: '#92400E',
              borderRadius: 20,
              padding: '3px 10px',
              fontSize: 11,
              fontWeight: 500,
              border: '1px solid #D4A017',
            }}>
              ★ Account creator
            </span>
          )}
        </div>

        {/* Full Name */}
        <div style={{ padding: '14px 16px 0' }}>
          <label style={{
            fontSize: 11, fontWeight: 600, color: '#6B7280',
            textTransform: 'uppercase', letterSpacing: '0.05em',
            display: 'block', marginBottom: 6,
          }}>
            Full Name
          </label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Enter full name"
            maxLength={80}
            style={{
              width: '100%', border: '1px solid #E5E7EB', borderRadius: 8,
              padding: '8px 10px', fontSize: 13, color: '#111827',
              fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none',
            }}
          />
        </div>

        {/* Relation */}
        <div style={{ padding: '14px 16px 0' }}>
          <label style={{
            fontSize: 11, fontWeight: 600, color: '#6B7280',
            textTransform: 'uppercase', letterSpacing: '0.05em',
            display: 'block', marginBottom: 6,
          }}>
            Relation to you
          </label>
          <input
            type="text"
            value={relation}
            onChange={e => setRelation(e.target.value)}
            placeholder="e.g. Father, Uncle, Cousin…"
            maxLength={50}
            style={{
              width: '100%', border: '1px solid #E5E7EB', borderRadius: 8,
              padding: '8px 10px', fontSize: 13, color: '#111827',
              fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none',
            }}
          />
          <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 3 }}>
            Start typing — e.g. Father, Mother, Uncle, Cousin
          </div>
        </div>

        {/* Date of Birth */}
        <div style={{ padding: '14px 16px 0' }}>
          <label style={{
            fontSize: 11, fontWeight: 600, color: '#6B7280',
            textTransform: 'uppercase', letterSpacing: '0.05em',
            display: 'block', marginBottom: 6,
          }}>
            Date of Birth
          </label>
          <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
            <button
              onClick={() => setDobKnownYear(true)}
              style={{
                padding: '4px 12px', fontSize: 11, borderRadius: 20, cursor: 'pointer',
                background: dobKnownYear ? '#0e0018' : '#F3F4F6',
                color: dobKnownYear ? '#fff' : '#6B7280',
                border: dobKnownYear ? '1px solid #0e0018' : '1px solid #E5E7EB',
              }}
            >
              Full date
            </button>
            <button
              onClick={() => setDobKnownYear(false)}
              style={{
                padding: '4px 12px', fontSize: 11, borderRadius: 20, cursor: 'pointer',
                background: !dobKnownYear ? '#0e0018' : '#F3F4F6',
                color: !dobKnownYear ? '#fff' : '#6B7280',
                border: !dobKnownYear ? '1px solid #0e0018' : '1px solid #E5E7EB',
              }}
            >
              Day &amp; month only
            </button>
          </div>
          {dobKnownYear ? (
            <input
              type="date"
              value={dob}
              onChange={e => setDob(e.target.value)}
              style={{
                width: '100%', border: '1px solid #E5E7EB', borderRadius: 8,
                padding: '8px 10px', fontSize: 13, color: '#111827',
                fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none',
              }}
            />
          ) : (
            <input
              type="text"
              value={dobMonthDay}
              onChange={e => setDobMonthDay(e.target.value)}
              placeholder="DD/MM e.g. 14/04"
              maxLength={5}
              style={{
                width: '100%', border: '1px solid #E5E7EB', borderRadius: 8,
                padding: '8px 10px', fontSize: 13, color: '#111827',
                fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none',
              }}
            />
          )}
          {!dobKnownYear && <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 3 }}>Day and month only — year unknown</div>}
        </div>

        {/* Special Notes */}
        <div style={{ padding: '14px 16px 0' }}>
          <label style={{
            fontSize: 11,
            fontWeight: 600,
            color: '#6B7280',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            display: 'block',
            marginBottom: 6,
          }}>
            Special Notes
          </label>
          <textarea
            value={notes}
            onChange={(e) => { if (e.target.value.length <= 100) setNotes(e.target.value); }}
            placeholder="e.g. Retired teacher, Mysuru"
            rows={2}
            style={{
              width: '100%',
              border: '1px solid #E5E7EB',
              borderRadius: 8,
              padding: '8px 10px',
              fontSize: 13,
              color: '#111827',
              resize: 'none',
              fontFamily: 'inherit',
              boxSizing: 'border-box',
              outline: 'none',
            }}
          />
          <div style={{ fontSize: 11, color: '#9CA3AF', textAlign: 'right', marginTop: 2 }}>
            {notes.length} / 100
          </div>
        </div>

        {/* Story */}
        <div style={{ padding: '10px 16px 0' }}>
          <label style={{
            fontSize: 11,
            fontWeight: 600,
            color: '#6B7280',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            display: 'block',
            marginBottom: 6,
          }}>
            Story / Biography
          </label>
          <textarea
            value={story}
            onChange={(e) => { if (e.target.value.length <= 2000) setStory(e.target.value); }}
            placeholder="Write this person's story — their life, memories, achievements…"
            rows={7}
            style={{
              width: '100%',
              border: '1px solid #E5E7EB',
              borderRadius: 8,
              padding: '8px 10px',
              fontSize: 13,
              color: '#111827',
              resize: 'vertical',
              fontFamily: 'inherit',
              boxSizing: 'border-box',
              outline: 'none',
              lineHeight: 1.5,
            }}
          />
          <div style={{
            fontSize: 11,
            color: story.length >= 1800 ? '#D97706' : '#9CA3AF',
            textAlign: 'right',
            marginTop: 2,
          }}>
            {story.length} / 2000
          </div>
        </div>

        {/* Save button */}
        <div style={{ padding: '12px 16px 0' }}>
          {saveMsg && (
            <div style={{
              fontSize: 12,
              color: saveMsg.includes('✓') ? '#059669' : '#DC2626',
              marginBottom: 8,
              textAlign: 'center',
            }}>
              {saveMsg}
            </div>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              width: '100%',
              background: saving ? '#9CA3AF' : '#D4A017',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '11px',
              fontWeight: 600,
              fontSize: 14,
              cursor: saving ? 'not-allowed' : 'pointer',
            }}
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>

        {/* Photo upload */}
        <div style={{ padding: '10px 16px 20px' }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handlePhotoUpload}
          />

          {member.profilePhotoUrl ? (
            <div style={{ textAlign: 'center' }}>
              <img
                src={member.profilePhotoUrl}
                alt="Profile"
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '2px solid #E5E7EB',
                  display: 'block',
                  margin: '0 auto 8px',
                }}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={photoUploading}
                style={{
                  background: 'none',
                  border: '1px solid #E5E7EB',
                  borderRadius: 6,
                  padding: '5px 12px',
                  fontSize: 11,
                  color: '#6B7280',
                  cursor: 'pointer',
                }}
              >
                {photoUploading ? 'Uploading…' : '🔄 Change photo'}
              </button>
            </div>
          ) : (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={photoUploading}
              style={{
                width: '100%',
                border: '1.5px dashed #D4A017',
                borderRadius: 8,
                padding: '10px',
                textAlign: 'center',
                color: '#D4A017',
                fontSize: 12,
                background: '#FFFBEB',
                cursor: 'pointer',
                fontWeight: 500,
              }}
            >
              {photoUploading ? 'Uploading…' : '📷 Upload profile photo'}
            </button>
          )}

          {photoMsg && (
            <div style={{
              fontSize: 11,
              color: photoMsg.includes('✓') ? '#059669' : '#DC2626',
              textAlign: 'center',
              marginTop: 6,
            }}>
              {photoMsg}
            </div>
          )}
        </div>

        {/* Story Photos */}
        <StoryPhotos
          member={member}
          familyEmail={familyEmail}
          onMemberUpdated={onMemberUpdated}
        />

      </div>
    </>
  );
}

function StoryPhotos({ member, familyEmail, onMemberUpdated }) {
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState('');
  const fileRef = React.useRef(null);
  const photos = member.storyPhotoUrls || [];
  const canUpload = photos.length < 5;

  async function handleUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setMsg('Please select an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { setMsg('Photo must be under 5MB'); return; }

    setUploading(true);
    setMsg('');
    try {
      const encoded = encodeURIComponent(familyEmail);
      const urlRes = await fetch(
        `${API}/api/members/${encoded}/${member.memberId}/story-photo-url`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fileName: `story_${Date.now()}.${file.name.split('.').pop()}`,
            fileType: file.type,
          }),
        }
      );
      if (!urlRes.ok) throw new Error('Could not get upload URL');
      const { signedUrl, accessToken } = await urlRes.json();

      const uploadRes = await fetch(signedUrl, {
        method: 'POST',
        headers: {
          'Content-Type': file.type,
          'Authorization': `Bearer ${accessToken}`,
          'X-Goog-Upload-Content-Type': file.type,
        },
        body: file,
      });
      if (!uploadRes.ok) throw new Error('Upload failed');

      setMsg('Photo added ✓');
      if (onMemberUpdated) onMemberUpdated();
    } catch (err) {
      console.error('Story photo error:', err);
      setMsg('Upload failed — try again');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  return (
    <div style={{ padding: '10px 16px 24px' }}>
      <label style={{
        fontSize: 11,
        fontWeight: 600,
        color: '#6B7280',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        display: 'block',
        marginBottom: 8,
      }}>
        Story Photos <span style={{ color: '#D1D5DB', fontWeight: 400 }}>({photos.length}/5)</span>
      </label>

      {/* Existing photos */}
      {photos.length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
          {photos.map((url, i) => (
            <img
              key={i}
              src={url}
              alt={`Story photo ${i + 1}`}
              style={{
                width: 72,
                height: 72,
                borderRadius: 6,
                objectFit: 'cover',
                border: '1px solid #E5E7EB',
              }}
            />
          ))}
        </div>
      )}

      {/* Upload button — only if under limit */}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleUpload}
      />
      {canUpload && (
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          style={{
            width: '100%',
            border: '1.5px dashed #D4A017',
            borderRadius: 8,
            padding: '9px',
            textAlign: 'center',
            color: '#D4A017',
            fontSize: 12,
            background: '#FFFBEB',
            cursor: uploading ? 'not-allowed' : 'pointer',
            fontWeight: 500,
          }}
        >
          {uploading ? 'Uploading…' : `📷 Add story photo (${5 - photos.length} remaining)`}
        </button>
      )}
      {!canUpload && (
        <div style={{ fontSize: 11, color: '#9CA3AF', textAlign: 'center' }}>
          Maximum 5 story photos per member
        </div>
      )}

      {msg && (
        <div style={{
          fontSize: 11,
          color: msg.includes('✓') ? '#059669' : '#DC2626',
          textAlign: 'center',
          marginTop: 6,
        }}>
          {msg}
        </div>
      )}
    </div>
  );
}
